import { useState, useRef, useCallback, useEffect } from 'react';
import GameEngine from '../game/GameEngine';
import Confetti from '../components/Confetti';
import AnimatedCounter from '../components/AnimatedCounter';
import { triggerToast } from '../components/Toast';
import { startSession, syncSession, endSession, isLoggedIn } from '../services/api';
import { hanumanJi } from '../assets/images';
import BackButton from '../components/BackButton';
import { GAME_LEVELS, LEVEL_THRESHOLDS } from '../game/constants';
import { useLang, localizePeeth, localizeJyotirlinga } from '../i18n';
import '../styles/game.css';

const CUMULATIVE = [0, ...LEVEL_THRESHOLDS]; // [0, 11, 53, 206, 610, 1165]

function getLevelInfo(totalRam) {
  for (let i = 0; i < GAME_LEVELS.length; i++) {
    if (totalRam < CUMULATIVE[i + 1]) {
      const level = GAME_LEVELS[i];
      const progressInLevel = totalRam - CUMULATIVE[i];
      const roundsCompleted = Math.floor(progressInLevel / level.chantsPerRound);
      const chantsInCurrentRound = progressInLevel % level.chantsPerRound;
      return {
        levelIndex: i,
        level,
        roundsCompleted,
        currentRound: Math.min(roundsCompleted + 1, level.rounds),
        chantsInCurrentRound,
        progressPct: Math.min(100, (progressInLevel / level.totalChants) * 100),
        jyotirlingaPhase: false,
      };
    }
  }
  return { levelIndex: 5, level: null, progressPct: 100, jyotirlingaPhase: true };
}

export default function GameScreen({ user, onBack, onUpdateUser }) {
  const { lang, t } = useLang();
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const sessionIdRef = useRef(null);
  const unsyncedRef = useRef(0);
  const totalRamRef = useRef(user?.total_ram_count || 0);
  const scoreRef = useRef(null);

  const [gameState, setGameState] = useState('start'); // start | playing | over
  const [ramCount, setRamCount] = useState(0);
  const [sessionStats, setSessionStats] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [shake, setShake] = useState(false);
  const [praharLabel, setPraharLabel] = useState('प्रभात');
  const [dialogueVisible, setDialogueVisible] = useState(false);

  /* Show dialogue strip briefly each time the message bucket advances
     (every 5 chants), then auto-dismiss. CRITICAL: depend ONLY on
     dialogueBucket — not ramCount — otherwise every tap's cleanup
     clears the 2.2s timer and the popup gets stuck visible forever
     during rapid tapping. */
  const dialogueBucket = Math.floor(ramCount / 5);
  useEffect(() => {
    if (gameState !== 'playing') return;
    if (dialogueBucket === 0) return;          // first message lands at chant 5
    setDialogueVisible(true);
    const id = setTimeout(() => setDialogueVisible(false), 2200);
    return () => clearTimeout(id);
  }, [dialogueBucket, gameState]);

  // Hide whenever we leave the 'playing' state (game over, navigated away)
  useEffect(() => {
    if (gameState !== 'playing') setDialogueVisible(false);
  }, [gameState]);

  // Auto-start game immediately on mount (skip the start overlay)
  useEffect(() => {
    startGame();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up engine on unmount
  useEffect(() => {
    return () => {
      if (engineRef.current) {
        engineRef.current.destroy();
      }
    };
  }, []);

  // Start a game session on the backend
  const beginSession = useCallback(async () => {
    if (!isLoggedIn()) return;
    try {
      const data = await startSession();
      sessionIdRef.current = data.session_id;
    } catch (err) {
      console.warn('Session start failed (playing offline):', err.message);
    }
  }, []);

  // Sync ram taps to backend (non-blocking)
  const doSync = useCallback(async (delta) => {
    if (!isLoggedIn() || !sessionIdRef.current || delta <= 0) return;
    try {
      const data = await syncSession(sessionIdRef.current, delta, 0);
      if (data.total_ram != null) {
        totalRamRef.current = data.total_ram;
      }
      if (data.unlocked_peeth) {
        triggerToast(`ॐ ${t('game.peethUnlocked', { name: localizePeeth(data.unlocked_peeth, lang).name })}`);
      }
      if (data.unlocked_jyotirlinga) {
        triggerToast(`🔱 ${t('game.jyotUnlocked', { name: localizeJyotirlinga(data.unlocked_jyotirlinga, lang).name })}`);
      }
    } catch (err) {
      console.warn('Sync failed:', err.message);
    }
  }, [t, lang]);

  // End session on backend
  const finishSession = useCallback(async (finalCount, reason = 'collision') => {
    if (!isLoggedIn() || !sessionIdRef.current) return;
    try {
      const data = await endSession(sessionIdRef.current, finalCount, reason);
      if (data.total_ram != null) {
        totalRamRef.current = data.total_ram;
        if (onUpdateUser) {
          onUpdateUser({
            total_ram_count: data.total_ram,
            shakti_peeths_visited: data.peeths_visited || 0,
          });
        }
      }
    } catch (err) {
      console.warn('Session end failed:', err.message);
    }
  }, [onUpdateUser]);

  // Initialize and start the game engine
  const startGame = useCallback(() => {
    setGameState('playing');
    setRamCount(0);
    setSessionStats(null);
    setShowConfetti(false);
    unsyncedRef.current = 0;

    beginSession();

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Destroy previous engine if any
    if (engineRef.current) {
      engineRef.current.destroy();
    }

    const engine = new GameEngine(canvas, {
      hanumanImgSrc: hanumanJi,
      onReady: () => {},
      onScore: (count) => {
        setRamCount(count);
        // Bounce score
        if (scoreRef.current) {
          scoreRef.current.classList.remove('bounce');
          void scoreRef.current.offsetWidth;
          scoreRef.current.classList.add('bounce');
        }
      },
      onGameOver: (stats) => {
        // Trigger screen shake
        setShake(true);
        setTimeout(() => setShake(false), 300);

        // Sync any remaining unsynced taps
        if (unsyncedRef.current > 0) {
          doSync(unsyncedRef.current);
          unsyncedRef.current = 0;
        }
        finishSession(stats.ramCount);
        setSessionStats(stats);
        setGameState('over');

        // Confetti on good scores
        if (stats.ramCount >= 20) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }
      },
      onMilestone: (milestone) => {
        triggerToast(milestone.message);
      },
      onPraharChange: (label) => {
        setPraharLabel(label);
      },
    });

    engineRef.current = engine;
    engine.start();
  }, [beginSession, doSync, finishSession]);

  const handleRamTap = useCallback((e) => {
    e.preventDefault();
    if (gameState !== 'playing' || !engineRef.current) return;

    engineRef.current.jump();

    if (navigator.vibrate) navigator.vibrate(10);

    unsyncedRef.current++;
    if (unsyncedRef.current >= 10) {
      const delta = unsyncedRef.current;
      unsyncedRef.current = 0;
      doSync(delta);
    }
  }, [gameState, doSync]);

  // Handle back — end session cleanly
  const handleBack = useCallback(() => {
    if (engineRef.current) {
      engineRef.current.destroy();
    }
    if (unsyncedRef.current > 0 && sessionIdRef.current) {
      doSync(unsyncedRef.current);
    }
    if (sessionIdRef.current && ramCount > 0) {
      finishSession(ramCount, 'quit');
    }
    onBack();
  }, [onBack, doSync, finishSession, ramCount]);

  const estimatedTotal = (user?.total_ram_count || 0) + ramCount;
  const levelInfo = getLevelInfo(estimatedTotal);
  const dialogueLines = t('game.dialogue');

  return (
    <div className={`game-container ${shake ? 'shake' : ''}`}>
      <canvas ref={canvasRef} className="game-canvas" onPointerDown={handleRamTap} />

      <Confetti active={showConfetti} />

      {/* HUD — Score + Level Progress (right side) */}
      {gameState === 'playing' && (
        <div className="game-hud">
          <div className="prahar-chip" key={praharLabel}>{praharLabel}</div>
          <div className="game-score" ref={scoreRef}>{ramCount}</div>
          <div className="game-score-label">{t('common.ramChants')}</div>
          {!levelInfo.jyotirlingaPhase && levelInfo.level && (
            <>
              <div className="hud-level-row">
                <span className="hud-level-badge">{t('game.levelBadge', { n: levelInfo.levelIndex + 1 })}</span>
                <span className="hud-round-info">{t('game.roundInfo', { current: levelInfo.currentRound, total: levelInfo.level.rounds })}</span>
              </div>
              <div className="hud-progress-bar">
                <div className="hud-progress-fill" style={{ width: `${levelInfo.progressPct}%` }} />
              </div>
            </>
          )}
          {levelInfo.jyotirlingaPhase && (
            <div className="hud-level-badge" style={{ color: '#D4AF37' }}>{t('game.jyotirlingaPhase')}</div>
          )}
        </div>
      )}

      {/* Hanuman dialogue strip — auto-dismisses after each message */}
      {gameState === 'playing' && ramCount > 0 && (
        <div className={`hanuman-dialogue-strip ${dialogueVisible ? 'show' : 'hide'}`}>
          <img src={hanumanJi} alt="हनुमान जी" className="hanuman-dialogue-face" />
          <div className="hanuman-dialogue-text" key={dialogueBucket}>
            {dialogueLines[Math.floor(ramCount / 5) % dialogueLines.length]}
          </div>
        </div>
      )}

      {/* Back Button */}
      <BackButton onClick={handleBack} game />

      {/* RAM Button — bottom right */}
      {gameState === 'playing' && (
        <div className="ram-button-container">
          <button
            className="ram-button"
            onPointerDown={handleRamTap}
          >
            <span className="ram-button-text">राम</span>
          </button>
        </div>
      )}

      {/* Game Over Modal */}
      {gameState === 'over' && sessionStats && (
        <div className="game-over-overlay">
          <div className="game-over-modal">
            <div className="game-over-title">{t('game.overTitle')}</div>
            <div className="game-over-subtitle">{t('game.overSubtitle')}</div>

            <div className="game-over-stats">
              <div className="game-over-stat">
                <span className="game-over-stat-label">{t('common.ramChants')}</span>
                <span className="game-over-stat-value">
                  <AnimatedCounter value={sessionStats.ramCount} duration={800} />
                </span>
              </div>
              <div className="game-over-stat">
                <span className="game-over-stat-label">{t('game.statObstacles')}</span>
                <span className="game-over-stat-value">
                  <AnimatedCounter value={sessionStats.obstaclesAvoided} duration={800} />
                </span>
              </div>
              <div className="game-over-stat">
                <span className="game-over-stat-label">{t('game.statTotalRam')}</span>
                <span className="game-over-stat-value">
                  <AnimatedCounter value={totalRamRef.current} duration={1200} />
                </span>
              </div>
            </div>

            <div className="game-over-buttons">
              <button className="replay-btn" onClick={startGame}>
                {t('game.replay')}
              </button>
              <button className="menu-btn" onClick={handleBack}>
                {t('game.mainMenu')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
